import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const sampleData = [
  { id: 1, label: "Per Google Review", defaultAmount: 0, backendField: "tasks.greenStars", markReadOnly: false },
  { id: 2, label: "Per FaceBook Review", defaultAmount: 0, backendField: "tasks.greenStars", markReadOnly: false },
  { id: 3, label: "Per New Testimonial", defaultAmount: 0, backendField: "tasks.greenStars", markReadOnly: false },
  { id: 4, label: "Per Video Recording", defaultAmount: 0, backendField: "tasks.greenStars", markReadOnly: false },
  { id: 5, label: "Per Patient Incentive", defaultAmount: 0, backendField: "att.workedDays", markReadOnly: false },
  { id: 6, label: "Per Patient Support Incentive", defaultAmount: 0, backendField: "att.workedDays", markReadOnly: false },
  { id: 7, label: "Per Checklist Incentive", defaultAmount: 0, backendField: "checkl.notCompletedTotal", markReadOnly: false },
  { id: 8, label: "One Time Task Incentive", defaultAmount: 0, backendField: "tasks.redStars", markReadOnly: true },
  { id: 9, label: "Per Working Sunday", defaultAmount: 0, backendField: "att.workedSundays", markReadOnly: true },
  { id: 10, label: "Per Working Half Day", defaultAmount: 0, backendField: "att.workedHalfDays", markReadOnly: true },
  { id: 11, label: "Absent Days", defaultAmount: 0, backendField: "att.absents", markReadOnly: true },
];

function getCurrentMonth() {
  var d = new Date();
  var m = d.getMonth() + 1;
  if (m < 10) m = "0" + m;
  return d.getFullYear() + "-" + m;
}

function getUserId(user) {
  if (!user) return null;
  return user._id || user.id || null;
}

function buildEmptyPayrollFromUser(user) {
  return {
    baseSalary: user.baseSalary || 0,
    bonuses: {
      joiningBonus: { amount: Number(user.joiningBonus || 0), mode: "one-time" },
      retentionBonus: { amount: Number(user.retentionBonus || 0), mode: "one-time" },
      referralBonus: { amount: Number(user.referralBonus || 0), mode: "one-time" },
      specialAllowance: { amount: Number(user.specialAllowance || 0), mode: "one-time" },
    },
    components: {},
    documents: [],
    notes: ""
  };
}

function getBackendValue(backendData, path) {
  if (!backendData || !path) return 0;
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : 0), backendData);
}

function getBonusForMonth(bonus) {
  const { amount = 0, mode = "one-time" } = bonus || {};
  switch (mode) {
    case "one-time": return amount;
    case "monthly": return amount / 12;
    case "quarterly": return amount / 4;
    case "bi-annual": return amount / 2;
    case "annual": return amount;
    default: return amount;
  }
}

function sumComponentsToBePaid(obj) {
  if (!obj) return 0;
  var s = 0;
  for (var p in obj) {
    if (!obj.hasOwnProperty(p)) continue;
    var ent = obj[p];
    if (typeof ent === "number") s += Number(ent || 0);
    else if (ent && typeof ent === "object") s += Number(ent.toBePaid || 0);
  }
  return s;
}

function sumBonusesToBePaid(bonuses) {
  if (!bonuses) return 0;
  var s = 0;
  for (var p in bonuses) {
    if (!bonuses.hasOwnProperty(p)) continue;
    s += Number(bonuses[p].toBePaid || 0);
  }
  return s;
}

export { sampleData, getUserId, getBonusForMonth, sumComponentsToBePaid, sumBonusesToBePaid, getBackendValue };

export default function usePayroll() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [payrollRecordId, setPayrollRecordId] = useState(null);
  const [workingDaysOfMonth, setWorkingDaysOfMonth] = useState(26);
  const [genericComponents, setGenericComponents] = useState([]);
  const [showSlip, setShowSlip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [slipHTML, setSlipHTML] = useState("");
  const [showThirdColumn, setShowThirdColumn] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [salaryState, setSalaryState] = useState({
    baseSalary: 0,
    bonuses: {
      joiningBonus: { amount: 0, mode: "one-time" },
      retentionBonus: { amount: 0, mode: "one-time" },
      referralBonus: { amount: 0, mode: "one-time" },
      specialAllowance: { amount: 0, mode: "one-time" },
    }
  });

  // Initialize generic components once
  useEffect(function () {
    var editable = sampleData.map(function (c) {
      return { id: c.id, label: c.label, amount: c.defaultAmount };
    });
    setGenericComponents(editable);
  }, []);

  // Fetch users from backend on mount
  useEffect(function () {
    api.get(`${process.env.REACT_APP_URL}/users/staff/all`)
      .then(function (res) {
        if (res && res.data) setUsers(res.data);
      })
      .catch(function (err) {
        console.error("Failed to fetch users", err);
      });
  }, []);

  // When selectedUser changes, load payroll for selectedMonth
  useEffect(function () {
    if (!selectedUser) return;
    setPayrollRecordId(null);
    setSalaryState((prev) => {
      const empty = buildEmptyPayrollFromUser(selectedUser);
      if (prev?.components) empty.components = prev.components;
      if (prev?.bonuses) empty.bonuses = prev.bonuses;
      if (prev?.baseSalary) empty.baseSalary = prev.baseSalary;
      return empty;
    });
  }, [selectedUser, activeTab, selectedMonth]); // eslint-disable-line

  function normalizePayroll(payroll = {}) {
    return {
      baseSalary: payroll.baseSalary ?? 0,
      components: (payroll.components || []).map(c => ({
        key: c.key ?? "",
        name: c.name ?? "",
        amount: c.amount ?? 0,
        toBePaid: c.toBePaid ?? 0
      })),
      retentionBonus: {
        amount: payroll.retentionBonus?.amount ?? 0,
        mode: payroll.retentionBonus?.mode ?? "monthly",
        toBePaid: payroll.retentionBonus?.toBePaid ?? 0
      },
      referralBonus: {
        amount: payroll.referralBonus?.amount ?? 0,
        mode: payroll.referralBonus?.mode ?? "one-time",
        toBePaid: payroll.referralBonus?.toBePaid ?? 0
      },
      specialAllowance: {
        amount: payroll.specialAllowance?.amount ?? 0,
        mode: payroll.specialAllowance?.mode ?? "one-time",
        toBePaid: payroll.specialAllowance?.toBePaid ?? 0
      },
      month: payroll.month ?? "",
      totalEarnings: payroll.totalEarnings ?? 0,
      netPay: payroll.netPay ?? 0,
      documents: payroll.documents ?? []
    };
  }

  const loadPayrollForMonth = useCallback(function (userOrId, month) {
    const userId = typeof userOrId === "string" ? userOrId : getUserId(userOrId);
    if (!userId) return;

    setLoading(true);

    api.get(`${process.env.REACT_APP_URL}/payroll/user/${userId}/${month}?_=${Date.now()}`)
      .then(res => {
        if (!res?.data?.success) throw new Error("Failed fetching user/payroll");
        const { user, payroll } = res.data;
        setSelectedUser(user);

        if (payroll) {
          const loaded = normalizePayroll(payroll);
          setSalaryState(loaded);
          setPayrollRecordId(payroll._id || payroll.id || null);
        } else {
          const empty = buildEmptyPayrollFromUser(user);
          const comps = {};
          for (let i = 0; i < genericComponents.length; i++) {
            const g = genericComponents[i];
            comps[g.id] = { amount: Number(g.amount || 0), toBePaid: Number(g.amount || 0) };
          }
          setSalaryState({ ...empty, components: comps });
          setPayrollRecordId(null);
        }
      })
      .catch(err => {
        console.error("Error loading payroll/user", err);
        if (userOrId && typeof userOrId !== "string") {
          const empty = buildEmptyPayrollFromUser(userOrId);
          const comps = {};
          for (let i = 0; i < genericComponents.length; i++) {
            const g = genericComponents[i];
            comps[g.id] = { amount: Number(g.amount || 0), toBePaid: Number(g.amount || 0) };
          }
          setSelectedUser(userOrId);
          setSalaryState({ ...empty, components: comps });
          setPayrollRecordId(null);
        }
      })
      .finally(() => setLoading(false));
  }, [genericComponents]); // eslint-disable-line

  function handleUserClick(user) {
    setSelectedUser(user);
    setActiveTab("personal");
    loadPayrollForMonth(user, selectedMonth);
  }

  function updateSelectedUserField(field, value) {
    if (!selectedUser) return;
    var copy = JSON.parse(JSON.stringify(selectedUser));
    copy[field] = value;
    setSelectedUser(copy);
  }

  function updateBonusField(bonusKey, field, value) {
    setSalaryState((prev) => {
      if (!prev) return prev;
      const copy = JSON.parse(JSON.stringify(prev));
      if (!copy.bonuses) {
        copy.bonuses = {
          joiningBonus: { amount: 0, mode: "one-time" },
          retentionBonus: { amount: 0, mode: "one-time" },
          referralBonus: { amount: 0, mode: "one-time" },
          specialAllowance: { amount: 0, mode: "one-time" },
        };
      }
      if (!copy.bonuses[bonusKey])
        copy.bonuses[bonusKey] = { amount: 0, mode: "one-time" };
      copy.bonuses[bonusKey][field] = field === "amount" ? Number(value || 0) : value;
      return copy;
    });
  }

  function updateComponentValue(id, amount) {
    setSalaryState((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      if (!updated.components) updated.components = [];

      const backendVal = getBackendValue(prev.backendData, sampleData.find(c => c.id === id)?.backendField);

      updated.components[id] = {
        amount: Number(amount || 0),
        label: sampleData[id - 1].label,
        toBePaid: Number(backendVal || 0) * Number(amount || 0)
      };

      let compSum = 0;
      Object.values(updated.components).forEach((c) => {
        compSum += Number(c?.toBePaid || 0);
      });

      let bonusSum = 0;
      if (updated.bonuses) {
        for (var bk in updated.bonuses) {
          bonusSum += Number(updated.bonuses[bk].toBePaid || 0);
        }
      }
      updated.totalSalary = Math.round((updated.baseSalary || 0) + bonusSum + compSum);
      return updated;
    });
  }

  function generateSalary() {
    if (!selectedUser) return alert("Select a user first");
    const userId = getUserId(selectedUser);
    if (!userId) return alert("Selected user missing ID");
    if (!salaryState) setSalaryState(buildEmptyPayrollFromUser(selectedUser));

    setLoading(true);
    const month = selectedMonth;

    Promise.all([
      api.get(`${process.env.REACT_APP_URL}/payroll/attendance/${userId}?month=${month}`)
        .then(r => r?.data || {}).catch(() => ({})),
      api.get(`${process.env.REACT_APP_URL}/payroll/checklists/${userId}?month=${month}`)
        .then(r => r?.data?.processed || []).catch(() => []),
      api.get(`${process.env.REACT_APP_URL}/payroll/tasks/${userId}?month=${month}`)
        .then(r => r?.data || {}).catch(() => ({ redStars: 0, greenStars: 0 }))
    ])
      .then(([att, checkl, tasks]) => {
        const notCompletedTotal = (checkl || []).reduce((sum, i) => sum + (i.notCompleted || 0), 0);
        const backendData = { att, tasks, checkl: { notCompletedTotal } };

        const getBackendValueSafe = (path) => {
          if (!path) return 0;
          return path.split(".").reduce((a, k) => a?.[k] ?? 0, backendData);
        };

        const compMap = {};
        sampleData.forEach(item => {
          const backendVal = getBackendValueSafe(item.backendField);
          const editableAmount = salaryState?.components?.[item.id]?.amount || 0;
          compMap[item.id] = { amount: editableAmount, label: item.label, toBePaid: backendVal * editableAmount };
        });

        const baseSalary = Number(salaryState?.baseSalary || selectedUser.baseSalary || 0);
        const perDay = baseSalary / 26;

        // Per Working Sunday (id: 9)
        if (compMap[9]) {
          const workedSundays = backendData.att?.workedSundays || 0;
          compMap[9].amount = perDay;
          compMap[9].toBePaid = workedSundays * perDay;
        }
        // Per Checklist Incentive (id: 7)
        if (compMap[7]) {
          compMap[7].toBePaid = notCompletedTotal > 0 ? 0 : Number(compMap[7].amount || 0);
        }
        // One Time Task Incentive / Penalty (id: 8)
        if (compMap[8]) {
          const red = tasks.redStars || 0;
          const paydaysLost = Math.floor(red / 6);
          compMap[8].toBePaid = -(paydaysLost * perDay);
        }
        // Absent Days deduction (id: 11)
        if (compMap[11]) {
          const abs = Number(att?.absents || 0);
          compMap[11].toBePaid = -(abs * perDay);
          setWorkingDaysOfMonth(26 - abs);
        }
        // Per Working Half Day (id: 10)
        if (compMap[10]) {
          const halfDays = Number(att?.workedHalfDays || 0);
          compMap[10].amount = perDay / 2;
          compMap[10].toBePaid = halfDays * (perDay / 2);
        }
        // Google / FB / Testimonial / Video reviews (ids: 1-4)
        for (let id of [1, 2, 3, 4]) {
          if (compMap[id]) {
            const stars = tasks.greenStars || 0;
            compMap[id].toBePaid = stars * compMap[id].amount;
          }
        }

        // BONUS CALC
        const bonuses = JSON.parse(JSON.stringify(salaryState?.bonuses || {}));
        let bonusesToAdd = 0;
        Object.keys(bonuses).forEach(key => {
          const b = bonuses[key];
          let pay = b.mode === "monthly" ? Math.round((Number(b.amount) || 0) / 12) : Number(b.amount || 0);
          if (key === "joiningBonus" && b.mode === "one-time") {
            const joinMonth = selectedUser.joiningDate?.slice(0, 7);
            if (joinMonth !== selectedMonth) pay = 0;
          }
          bonuses[key].toBePaid = pay;
          bonusesToAdd += pay;
        });

        const compTotal = Object.values(compMap).reduce((sum, c) => sum + Number(c.toBePaid || 0), 0);
        const finalTotal = Math.round(baseSalary + bonusesToAdd + compTotal);

        setSalaryState({
          ...salaryState,
          baseSalary,
          bonuses,
          components: compMap,
          totalSalary: finalTotal,
          perDaySalary: perDay.toFixed(2),
          backendData,
        });
        setShowThirdColumn(true);
      })
      .finally(() => setLoading(false));
  }

  function saveSalaryStructure() {
    if (!selectedUser) return;
    let componentsArray = [];
    if (salaryState.components && typeof salaryState.components === "object") {
      componentsArray = Object.entries(salaryState.components).map(
        ([key, val]) => ({
          key,
          name: (sampleData.find(s => String(s.id) === String(key)) || {}).label || key,
          amount: val.amount ?? 0,
          toBePaid: val.toBePaid ?? 0
        })
      );
    }
    const payload = {
      baseSalary: salaryState.baseSalary,
      components: componentsArray,
      bonuses: salaryState.bonuses,
      month: selectedMonth,
    };
    api.post(`${process.env.REACT_APP_URL}/payroll/save/${getUserId(selectedUser)}`, payload)
      .then(() => alert("Salary structure saved for future months"))
      .catch(err => { console.error(err); alert("Error saving salary structure"); });
  }

  function savePayroll() {
    if (!selectedUser || !salaryState) return alert("Nothing to save");
    var userId = getUserId(selectedUser);
    if (!userId) return alert("Invalid user id");
    if (!selectedMonth) return alert("Please select a month");

    setSaving(true);

    var compArray = sampleData.map(function (sd) {
      var compEntry = salaryState.components && salaryState.components[sd.id];
      if (!compEntry && Array.isArray(salaryState.components)) {
        compEntry = salaryState.components.find(c => String(c.key) === String(sd.id));
      }
      return {
        key: String(sd.id),
        name: sd.label,
        amount: Number((compEntry && compEntry.amount) || 0),
        toBePaid: Number((compEntry && compEntry.toBePaid) || 0),
      };
    });

    var payload = {
      baseSalary: salaryState.baseSalary || 0,
      components: compArray,
      bonuses: salaryState.bonuses || {},
      retentionBonus: salaryState.bonuses?.retentionBonus || { amount: 0, mode: "one-time" },
      referralBonus: salaryState.bonuses?.referralBonus || { amount: 0, mode: "one-time" },
      specialAllowance: salaryState.bonuses?.specialAllowance || { amount: 0, mode: "one-time" },
      month: selectedMonth,
      totalEarnings: salaryState.totalSalary || 0,
      netPay: salaryState.totalSalary || 0,
    };

    api.post(`${process.env.REACT_APP_URL}/payroll/save/${userId}`, payload)
      .then(function () {
        alert("Payroll saved successfully");
        loadPayrollForMonth(selectedUser, selectedMonth);
      })
      .catch(function (err) {
        console.error("Save payroll error", err);
        alert("Failed to save payroll");
      })
      .finally(function () { setSaving(false); });
  }

  function handlePrintSlip() {
    const printArea = document.getElementById("print-area");
    printArea.innerHTML = slipHTML;
    setTimeout(() => { window.print(); }, 150);
  }

  function printSalarySlip() {
    if (!salaryState || !selectedUser) return alert("No salary data available.");
    const html = generateSalarySlipHTML(selectedUser, salaryState, selectedMonth);
    setSlipHTML(html);
    setShowSlipModal(true);
  }

  function generateSalarySlipHTML(user, state, month) {
    const componentsHTML = Object.keys(state.components || {})
      .map((key) => {
        const entry = state.components[key];
        const label = (sampleData.find(s => String(s.id) === String(key)) || {}).label || key;
        const amt = entry?.amount ?? 0;
        const toBe = entry?.toBePaid ?? 0;
        return `<tr><td>${label}</td><td style="text-align:right">\u20B9${amt}</td><td style="text-align:right">\u20B9${toBe}</td></tr>`;
      }).join("");

    const bonusesHTML = (state.bonuses ? Object.keys(state.bonuses).map((bk) => {
      const b = state.bonuses[bk] || {};
      return `<tr><td style="font-weight:600">${bk}</td><td style="text-align:right">\u20B9${Number(b.amount || 0)}</td><td style="text-align:right">\u20B9${Number(b.toBePaid || 0)}</td></tr>`;
    }).join("") : "");

    return `<html><head><title>Salary Slip - ${user.username}</title>
<style>body{font-family:Arial,sans-serif;padding:40px;background:#f7f7f7}.slip-container{width:700px;margin:auto;background:#fff;border:1px solid #ccc;padding:30px;border-radius:8px}.header{text-align:center;border-bottom:2px solid #444;padding-bottom:10px;margin-bottom:20px}.company-name{font-size:26px;font-weight:700}.title{font-size:20px;font-weight:600;margin-top:10px}.info-table,.salary-table{width:100%;border-collapse:collapse;margin-top:20px}.info-table td{padding:6px 0}.salary-table th{background:#efefef;padding:10px;border:1px solid #ccc}.salary-table td{padding:8px;border:1px solid #ccc}.total{text-align:right;font-size:18px;font-weight:700;margin-top:20px}.footer{margin-top:40px;font-size:13px;text-align:center;color:#666}</style>
</head><body><div class="slip-container">
<div class="header"><div class="company-name">Your Clinic / Company Name</div><div class="title">Salary Slip</div><div>${month}</div></div>
<table class="info-table"><tr><td><strong>Employee Name:</strong></td><td>${user.username}</td></tr><tr><td><strong>Email:</strong></td><td>${user.email ?? ""}</td></tr><tr><td><strong>Joining Date:</strong></td><td>${user.joiningDate || "-"}</td></tr><tr><td><strong>Bank:</strong></td><td>${user.bname || "-"}</td></tr><tr><td><strong>Account No:</strong></td><td>${user.bacct || "-"}</td></tr><tr><td><strong>IFSC:</strong></td><td>${user.bifsc || "-"}</td></tr></table>
<h3>Earnings Breakdown</h3>
<table class="salary-table"><thead><tr><th>Component</th><th>Amount</th><th>To Be Paid</th></tr></thead><tbody>
<tr><td>Base Salary</td><td style="text-align:right">\u20B9${state.baseSalary}</td><td style="text-align:right">\u20B9${state.baseSalary}</td></tr>
${bonusesHTML}${componentsHTML}</tbody></table>
<div class="total">Net Payable: \u20B9${state.totalSalary}</div>
<div class="footer">This is a computer-generated salary slip. No signature is required.</div>
</div></body></html>`;
  }

  function isCurrentMonth(month) {
    return month === getCurrentMonth();
  }

  function payrollStatusForMonth() {
    if (!selectedUser || !selectedMonth) return "canGenerate";
    const joiningMonth = selectedUser.joiningDate?.slice(0, 7);
    if (joiningMonth && selectedMonth < joiningMonth) return "beforeJoining";
    if (salaryState && payrollRecordId) return "exists";
    return "canGenerate";
  }

  // Navigate month
  function prevMonth() {
    setSelectedMonth(prev => {
      const [y, m] = prev.split('-').map(Number);
      const d = new Date(y, m - 2, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  }

  function nextMonth() {
    setSelectedMonth(prev => {
      const [y, m] = prev.split('-').map(Number);
      const d = new Date(y, m, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  }

  // Filtered users for search
  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const name = (u.username || u.fullName || u.name || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  function savePersonalInfo() {
    var uid = getUserId(selectedUser);
    if (!uid) return alert("User id missing");
    api.post(`${process.env.REACT_APP_URL}/payroll/save/${uid}`, selectedUser)
      .then(function () { alert("Personal info saved"); })
      .catch(function (e) { console.error(e); alert("Failed saving personal info"); });
  }

  return {
    // State
    users, filteredUsers, selectedUser, activeTab, selectedMonth,
    payrollRecordId, workingDaysOfMonth, loading, saving,
    showSlipModal, slipHTML, showThirdColumn, salaryState,
    searchTerm, showSlip,

    // Setters
    setActiveTab, setSelectedMonth, setShowSlipModal, setSalaryState,
    setSearchTerm,

    // Actions
    handleUserClick, updateSelectedUserField, updateBonusField,
    updateComponentValue, generateSalary, saveSalaryStructure,
    savePayroll, printSalarySlip, handlePrintSlip, savePersonalInfo,
    loadPayrollForMonth, prevMonth, nextMonth,

    // Helpers
    isCurrentMonth, payrollStatusForMonth, getUserId, getBonusForMonth,
    sumComponentsToBePaid, sumBonusesToBePaid, getBackendValue,

    // Data
    sampleData,
  };
}
